import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Pencil } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { Separator } from "@/shared/components/ui/separator";
import { useCurrentUser } from "@/shared/auth/useAuth";
import { Role } from "@/shared/types/role";
import { useFeatureFlagDetail } from "./use-feature-flag-detail";
import { useFeatureFlagHistory } from "./use-feature-flag-history";
import { FeatureFlagTypeBadge } from "./FeatureFlagTypeBadge";
import { FeatureFlagVisibilityBadge } from "./FeatureFlagVisibilityBadge";
import { FeatureFlagValueDisplay } from "./FeatureFlagValueDisplay";
import { FeatureFlagHistoryTable } from "./FeatureFlagHistoryTable";
import { EditFeatureFlagDialog } from "./EditFeatureFlagDialog";

const HISTORY_PAGE_SIZE = 20;

function formatDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function FeatureFlagDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const currentUser = useCurrentUser();
  const canEdit = currentUser?.role === Role.WANDERFIT_ADMIN;

  const { data: flag, isLoading, isError, error } = useFeatureFlagDetail(id);
  const [editOpen, setEditOpen] = useState(false);
  const [historyPage, setHistoryPage] = useState(0);
  const historyOffset = historyPage * HISTORY_PAGE_SIZE;

  const { data: history, isLoading: historyLoading } = useFeatureFlagHistory({
    id,
    limit: HISTORY_PAGE_SIZE,
    offset: historyOffset,
  });

  if (isLoading) {
    return (
      <div data-testid="feature-flag-detail-loading" className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (isError) {
    const status = (error as { status?: number } | null)?.status;
    return (
      <div data-testid="feature-flag-detail-error" className="space-y-4">
        <Link
          to="/feature-flags"
          className="inline-flex items-center gap-1 font-sans text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("featureFlags.detail.back")}
        </Link>
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-center">
          <p className="font-sans text-sm font-medium text-destructive">
            {status === 404 ? t("featureFlags.detail.notFound") : t("featureFlags.detail.error")}
          </p>
        </div>
      </div>
    );
  }

  if (!flag) return null;

  const totalHistory = history?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalHistory / HISTORY_PAGE_SIZE));
  const hasPrev = historyPage > 0;
  const hasNext = historyOffset + HISTORY_PAGE_SIZE < totalHistory;

  return (
    <div data-testid="feature-flag-detail-screen" className="space-y-6">
      <div>
        <Link
          to="/feature-flags"
          data-testid="feature-flag-detail-back"
          className="inline-flex items-center gap-1 font-sans text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("featureFlags.detail.back")}
        </Link>
      </div>

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 space-y-2">
          <p className="brand-eyebrow">{t("featureFlags.detail.eyebrow")}</p>
          <h1 className="break-all font-mono text-3xl font-semibold">{flag.id}</h1>
          <div className="flex flex-wrap items-center gap-2">
            <FeatureFlagTypeBadge valueType={flag.valueType} />
            <FeatureFlagVisibilityBadge isPublic={flag.isPublic} />
          </div>
          <p className="max-w-2xl font-sans text-base font-normal text-muted-foreground">
            {flag.description}
          </p>
        </div>

        {canEdit && (
          <Button data-testid="open-edit-feature-flag" onClick={() => setEditOpen(true)}>
            <Pencil className="mr-2 h-4 w-4" />
            {t("featureFlags.actions.edit")}
          </Button>
        )}
      </header>

      <section
        data-testid="feature-flag-detail-values"
        className="rounded-lg border border-border bg-card p-6"
      >
        <h2 className="brand-eyebrow mb-4">{t("featureFlags.detail.valueSection")}</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <p className="font-sans text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t("featureFlags.detail.currentValue")}
            </p>
            <FeatureFlagValueDisplay
              testId="feature-flag-current-value"
              value={flag.value}
              valueType={flag.valueType}
              className="mt-1"
            />
          </div>
          <div>
            <p className="font-sans text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t("featureFlags.detail.defaultValue")}
            </p>
            <FeatureFlagValueDisplay
              testId="feature-flag-default-value"
              value={flag.defaultValue}
              valueType={flag.valueType}
              className="mt-1"
            />
          </div>
        </div>
        <Separator className="my-4" />
        <dl className="grid grid-cols-1 gap-3 font-sans text-sm sm:grid-cols-2">
          <div>
            <dt className="font-medium text-muted-foreground">
              {t("featureFlags.detail.createdAt")}
            </dt>
            <dd className="mt-0.5 font-normal text-foreground">{formatDateTime(flag.createdAt)}</dd>
          </div>
          <div>
            <dt className="font-medium text-muted-foreground">
              {t("featureFlags.detail.updatedAt")}
            </dt>
            <dd className="mt-0.5 font-normal text-foreground">{formatDateTime(flag.updatedAt)}</dd>
          </div>
        </dl>
      </section>

      <section
        data-testid="feature-flag-detail-history"
        className="rounded-lg border border-border bg-card p-6"
      >
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <h2 className="brand-eyebrow">{t("featureFlags.detail.historySection")}</h2>
          {totalHistory > 0 && (
            <p className="font-sans text-xs font-normal text-muted-foreground">
              {t("featureFlags.history.total", { count: totalHistory })}
            </p>
          )}
        </div>

        {historyLoading && !history ? (
          <div className="space-y-2">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : (
          <FeatureFlagHistoryTable
            items={history?.items ?? flag.lastChanges}
            valueType={flag.valueType}
          />
        )}

        {totalHistory > HISTORY_PAGE_SIZE && (
          <div className="mt-4 flex items-center justify-between">
            <p className="font-sans text-xs font-normal text-muted-foreground">
              {t("featureFlags.history.pageOf", {
                page: historyPage + 1,
                total: totalPages,
              })}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                data-testid="feature-flag-history-prev"
                onClick={() => setHistoryPage((p) => Math.max(0, p - 1))}
                disabled={!hasPrev}
              >
                {t("featureFlags.history.previous")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                data-testid="feature-flag-history-next"
                onClick={() => setHistoryPage((p) => p + 1)}
                disabled={!hasNext}
              >
                {t("featureFlags.history.next")}
              </Button>
            </div>
          </div>
        )}
      </section>

      <EditFeatureFlagDialog
        flag={flag}
        open={editOpen}
        onOpenChange={setEditOpen}
        onSuccess={() => setEditOpen(false)}
      />
    </div>
  );
}
